import { useState, useEffect, useCallback } from "react";
import {
  MDBSideNavLink,
  MDBSideNavCat,
  MDBSideNavNav,
  MDBSideNav,
  MDBAnimation,
  MDBProgress,
  MDBIcon,
} from "mdbreact";
import { useSelector } from "react-redux";
import { Access, Policy, Sidebars } from "../../services/fakeDb";
import { getPlatformSidebar } from "../../services/fakeDb/sidebars";
import {
  Cloudinary,
  // ENDPOINT,
  FailedLogo,
  capitalize,
  // isImageValid,
} from "../../services/utilities";
import "./style.css";
import { BgRemover } from "../../components/images/index";
import Hotline from "../../pages/platforms/hotline";
const normalizePlatform = Access.normalizePlatformKey;
const diagnostics = [
  "diagnostic",
  "clinic",
  "laboratory",
  "radiology",
  "pharmacy",
  "infirmary",
  "hospital",
  "rehabilitation",
];
export default function SideNavigation({
  triggerOpening,
  breakWidth,
  onLinkClick,
}) {
  const [links, setLinks] = useState([]);
  // const [logo, setLogo] = useState(FailedLogo);
  // const [href, setHref] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [isDiagnostics, setIsDiagnostics] = useState(true);
  const { activePlatform, company, isLoading, branches = [] } = useSelector(
    ({ auth }) => auth,
  );
  const branchName =
    activePlatform?.branch?.name || activePlatform?.branch?.displayname || "";
  const isMainBranch = Boolean(
    activePlatform?.branch?.isMain ||
      String(activePlatform?.branch?.displayname || "")
        .trim()
        .toLowerCase() === "main",
  );
  const sidebarTitle = branchName
    ? `${branchName}${isMainBranch ? " Main" : ""} Branch`
    : company?.name || activePlatform?.branch?.company || "";

  useEffect(() => {
    if (diagnostics.includes(activePlatform?.branch?.category?.toLowerCase())) {
      setIsDiagnostics(true);
    } else {
      setIsDiagnostics(false);
    }
  }, [activePlatform]);

  // 🔧 Utility: Filter sidebar by role (recursive)
  const filterSidebarByRole = useCallback((items, role) => {
    return items
      .filter((item) => !item.allowedFor || item.allowedFor.includes(role))
      .map((item) => ({
        ...item,
        children: item.children
          ? filterSidebarByRole(item.children, role)
          : undefined,
      }));
  }, []);
  // 🔧 Utility: Filter sidebar by role (recursive)
  const filterSidebarByDepartment = useCallback((items, department) => {
    return items
      .filter(
        (item) => !item.allowedFor || item.allowedFor.includes(department),
      )
      .map((item) => ({
        ...item,
        children: item.children
          ? filterSidebarByDepartment(item.children, department)
          : undefined,
      }));
  }, []);

  // ✅ Guarded sidebar loader with platform/role filtering
  useEffect(() => {
    const platformKey = normalizePlatform(activePlatform?.platform);
    if (!platformKey) {
      const newLinks = Sidebars["patron"] || [];
      if (JSON.stringify(links) !== JSON.stringify(newLinks)) {
        setLinks(newLinks);
      }
      return;
    }
    const fullSidebar = getPlatformSidebar(platformKey, {
      isDiagnostics,
      activePlatform,
      branches,
    });
    if (platformKey === "laboratory") {
      const role = activePlatform?.role;
      const filtered = filterSidebarByRole(fullSidebar, role);
      if (JSON.stringify(links) !== JSON.stringify(filtered)) {
        setLinks(filtered);
      }
    } else if (platformKey === "frontdesk") {
      const department = Policy.getDepartment(activePlatform?.position);
      const filtered = filterSidebarByDepartment(fullSidebar, department);
      if (JSON.stringify(links) !== JSON.stringify(filtered)) {
        setLinks(filtered);
      }
    } else {
      if (JSON.stringify(links) !== JSON.stringify(fullSidebar)) {
        setLinks(fullSidebar);
      }
    }
  }, [
    activePlatform,
    branches,
    company,
    links,
    filterSidebarByRole,
    filterSidebarByDepartment,
    isDiagnostics,
  ]);

  // 🔁 Recursive nav render
  const renderNavItems = (
    _links,
    keyPrefix = "",
    basePath = "",
    level = 1.5,
  ) => {
    return _links.map((item, index) => {
      const key = `${keyPrefix}-${index}`;
      const fullPath = `${basePath}${item.path || ""}`;
      const isOpen = activeCategory === key;
      const indentStyle = { paddingLeft: `${level * 15}px` };

      if (item.children && item.children.length > 0) {
        return (
          <MDBSideNavCat
            id={`${key}-cat`}
            name={capitalize(item.name)}
            key={`${key}-cat`}
            icon={item.icon}
            isOpen={isOpen}
            onClick={() =>
              setActiveCategory((prev) => (prev === key ? "" : key))
            }
            style={indentStyle}
          >
            {renderNavItems(
              item.children,
              key,
              normalizePlatform(fullPath),
              level + 1,
            )}
          </MDBSideNavCat>
        );
      }

      return (
        <MDBSideNavLink
          key={key}
          to={normalizePlatform(fullPath)}
          topLevel
          onClick={onLinkClick}
          style={indentStyle}
          id="sidebar-link"
          title={item.title}
        >
          <MDBIcon icon={item.icon} className="mr-2" />
          {capitalize(item.name)}
        </MDBSideNavLink>
      );
    });
  };

  return (
    <div className="white-skin no-print">
      <MDBSideNav
        tag="div"
        bg="https://mdbootstrap.com/img/Photos/Others/sidenav2.jpg"
        alt="Electronic Health Record System"
        mask="strong"
        href={"#"}
        fixed
        breakWidth={breakWidth}
        triggerOpening={triggerOpening}
        style={{ transition: "padding-left .3s" }}
      >
        {/* Header: the logo banner is as tall as the top bar, with the
            branch name in a full-width blue strip under it. */}
        <div className="sidebar-banner">
          <BgRemover
            src={`${Cloudinary.getEndpoint()}/${company?.lid || ""}/companies/${
              company?.name
            }/logo.png`}
            fallback={FailedLogo}
            className="sidebar-banner__logo"
          />
        </div>
        <div className="sidebar-title">{sidebarTitle}</div>
        {/* Nav */}
        <MDBSideNavNav>
          {!isLoading
            ? renderNavItems(
                links,
                "sidebar",
                `/${normalizePlatform(activePlatform?.platform || "patron")}`,
              )
            : new Array(6).fill().map((_, index) => (
                <div className="mx-2" key={index}>
                  <MDBAnimation
                    type="flash"
                    infinite
                    className="mt-3 d-flex align-items-center"
                    delay={`${index + 1}00ms`}
                    duration="3000ms"
                  >
                    <MDBProgress
                      animated
                      id={`sidebar-loading-icon`}
                      color="light"
                      className="mr-2 ml-2"
                      value={3000}
                    ></MDBProgress>
                    <MDBProgress
                      animated
                      className="mr-2"
                      id={`sidebar-loading-${index + 1}`}
                      color="light"
                      value={3000}
                    ></MDBProgress>
                  </MDBAnimation>
                </div>
              ))}
        </MDBSideNavNav>
        <Hotline />
      </MDBSideNav>
    </div>
  );
}
